import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { account, team, untilDay, untilWhen, wordFor } from "../account.ts";
import type { LinkInfo, SignedIn } from "../account.ts";
import { useStore } from "../hooks.ts";
import { ApiError, api } from "../store.ts";
import { AuthPage, Login, messageOf } from "./Login.tsx";

function useLink(token: string) {
  const [info, setInfo] = useState<LinkInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    team.check(token).then(setInfo, (err) => setError(messageOf(err)));
  }, [token]);
  return { info, error };
}

function Invalid({ error, forgot = false }: { error: string; forgot?: boolean }) {
  return (
    <AuthPage>
      <div className="card">
        <p className="error-box">{error}</p>
        {forgot && <p className="hint">Einen neuen Link per E-Mail gibt es bei der Anmeldung unter „Passwort vergessen?“.</p>}
        <a className="button" href="#/anmelden">
          Zur Anmeldung
        </a>
      </div>
    </AuthPage>
  );
}

/**
 * "#/einladung/<token>": the invited person creates an account (name given by the owner, user
 * name and password their own), or joins with the account this device is signed in with.
 */
export function Join({ token, onSignedIn }: { token: string; onSignedIn: (s: SignedIn) => void }) {
  const { store } = useStore();
  const { info, error } = useLink(token);
  const [form, setForm] = useState({ displayName: "", email: "", username: "", password: "", repeat: "" });
  const [fresh, setFresh] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (info?.purpose === "join") setForm((f) => ({ ...f, displayName: f.displayName || info.name }));
  }, [info]);

  if (error) return <Invalid error={error} />;
  if (!info) return <AuthPage>{<p className="muted">Lädt …</p>}</AuthPage>;
  if (info.purpose !== "join") return <Invalid error="Dieser Link ist keine Einladung." />;
  const word = wordFor(info.kind);
  const signedIn = store.config && !store.config.token;

  const field = (key: keyof typeof form) => ({ value: form[key], onChange: (e: { target: { value: string } }) => setForm({ ...form, [key]: e.target.value }) });

  async function submit(e: FormEvent) {
    e.preventDefault();
    setProblem(null);
    if (form.password.length < 10) return setProblem("Das Passwort braucht mindestens 10 Zeichen.");
    if (form.password !== form.repeat) return setProblem("Die beiden Passwörter sind nicht gleich.");
    setBusy(true);
    try {
      const { repeat: _repeat, ...body } = form;
      const done = await team.join(token, { ...body, username: body.username.trim() });
      // A device that held another account starts fresh.
      if (store.config) await store.signOut();
      onSignedIn(done);
    } catch (err) {
      setProblem(messageOf(err));
    } finally {
      setBusy(false);
    }
  }

  async function joinWithAccount() {
    setBusy(true);
    setProblem(null);
    try {
      await team.joinWithAccount(token);
      location.hash = "#/konto";
    } catch (err) {
      setProblem(messageOf(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthPage>
      <div className="card">
        <h2>Willkommen, {info.name}!</h2>
        <p>
          Du bist in {word.the} <strong>{info.workspace}</strong> eingeladen. Leg dir ein Konto an, dann bist du gleich angemeldet. Der Link gilt bis {untilDay(info.expiresAt)}.
        </p>
        {signedIn && !fresh ? (
          <>
            <p>
              Auf diesem Gerät bist du schon angemeldet ({store.config!.workspace}). Mit deinem Konto beitreten, oder ein neues anlegen?
            </p>
            <button className="primary wide" disabled={busy} onClick={joinWithAccount} data-testid="join-with-account">
              Mit meinem Konto beitreten
            </button>
            <button className="wide" onClick={() => setFresh(true)}>
              Neues Konto anlegen
            </button>
          </>
        ) : (
          <form noValidate onSubmit={submit}>
            <label>
              Dein Name
              <input {...field("displayName")} maxLength={40} autoComplete="nickname" data-testid="display-name" />
              <span className="hint">So nennen dich die anderen in der App.</span>
            </label>
            <label>
              E-Mail-Adresse
              <input {...field("email")} type="email" maxLength={200} autoComplete="email" data-testid="email" />
              <span className="hint">Damit du dein Passwort selbst zurücksetzen kannst. Nie für Werbung.</span>
            </label>
            <label>
              Benutzername
              <input {...field("username")} maxLength={64} autoComplete="username" autoCapitalize="none" autoCorrect="off" spellCheck={false} data-testid="new-username" />
              <span className="hint">Zum Anmelden. Darf auch deine E-Mail-Adresse sein.</span>
            </label>
            <label>
              Passwort
              <input {...field("password")} type="password" autoComplete="new-password" data-testid="new-password" />
              <span className="hint">Mindestens 10 Zeichen.</span>
            </label>
            <label>
              Passwort wiederholen
              <input {...field("repeat")} type="password" autoComplete="new-password" data-testid="repeat-password" />
            </label>
            {problem && <p className="error-box">{problem}</p>}
            <button className="primary" disabled={busy} data-testid="join">
              {busy ? "Konto anlegen …" : "Konto anlegen"}
            </button>
          </form>
        )}
        {problem && signedIn && !fresh && <p className="error-box">{problem}</p>}
      </div>
    </AuthPage>
  );
}

/** "#/passwort/<token>": a new password through the link the owner made, or one by mail („Passwort vergessen?“). */
export function Reset({ token, onSignedIn }: { token: string; onSignedIn: (s: SignedIn) => void }) {
  const { store } = useStore();
  const { info, error } = useLink(token);
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  // Removing is preselected after an owner's link, not after one by mail (sections 18 and 19 of the shared spec).
  const [chosen, setRemovePasskeys] = useState<boolean | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (error) return <Invalid error={error} forgot />;
  if (!info) return <AuthPage>{<p className="muted">Lädt …</p>}</AuthPage>;
  if (info.purpose !== "reset") return <Invalid error="Dieser Link setzt kein Passwort." />;
  const passkeys = info.passkeys;
  const removePasskeys = chosen ?? !info.self;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setProblem(null);
    if (password.length < 10) return setProblem("Das Passwort braucht mindestens 10 Zeichen.");
    if (password !== repeat) return setProblem("Die beiden Passwörter sind nicht gleich.");
    setBusy(true);
    try {
      const done = await team.reset(token, password, passkeys > 0 && removePasskeys);
      onSignedIn(done);
    } catch (err) {
      setProblem(messageOf(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthPage>
      <div className="card">
        <h2>Neues Passwort</h2>
        <p>
          Für {info.name}, Benutzername <strong>{info.username}</strong>. Der Link gilt bis {untilWhen(info.expiresAt)}.
        </p>
        {store.config && !store.config.token && <p className="notice">Auf diesem Gerät ist jemand angemeldet. Nach dem Speichern bist du als {info.name} angemeldet.</p>}
        <form noValidate onSubmit={submit}>
          <input type="text" name="username" autoComplete="username" value={info.username} readOnly hidden />
          <label>
            Neues Passwort
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" data-testid="new-password" />
            <span className="hint">Mindestens 10 Zeichen.</span>
          </label>
          <label>
            Neues Passwort wiederholen
            <input type="password" value={repeat} onChange={(e) => setRepeat(e.target.value)} autoComplete="new-password" data-testid="repeat-password" />
          </label>
          {info.passkeys > 0 && (
            <>
              <label className="check">
                <input type="checkbox" checked={removePasskeys} onChange={(e) => setRemovePasskeys(e.target.checked)} data-testid="remove-passkeys" />
                Alle Passkeys entfernen ({info.passkeys})
              </label>
              <p className="hint">Wenn ein Gerät verloren ging oder jemand anderes in dein Konto kam. Passkeys auf deinen Geräten legst du danach unter „Mein Konto“ neu an.</p>
            </>
          )}
          {problem && <p className="error-box">{problem}</p>}
          <button className="primary" disabled={busy} data-testid="save-password">
            {busy ? "Passwort speichern …" : "Passwort speichern"}
          </button>
        </form>
      </div>
    </AuthPage>
  );
}

/**
 * "#/email/<token>": confirms the address the link in the mail went to, only signed in as the
 * account it was made for (Martin, 25.10.2026): without a session the page asks for the sign-in
 * first and confirms afterwards; signed in as someone else it confirms nothing, the link stays.
 */
export function ConfirmEmail({ token, onSignedIn }: { token: string; onSignedIn: (s: SignedIn) => void }) {
  const { store } = useStore();
  const [phase, setPhase] = useState<"busy" | "sign_in" | { kind: "ok" | "error" | "other"; text: string }>("busy");
  const [signed, setSigned] = useState<SignedIn | null>(null);

  async function confirmIt() {
    setPhase("busy");
    try {
      await account.confirmEmail(token);
      setPhase({ kind: "ok", text: "Deine E-Mail-Adresse ist bestätigt. Damit kannst du dein Passwort selbst zurücksetzen." });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return setPhase("sign_in");
      setPhase({ kind: err instanceof ApiError && err.status === 403 ? "other" : "error", text: messageOf(err) });
    }
  }
  useEffect(() => {
    void confirmIt();
  }, [token]);

  /** Signed in as the wrong account: out, then the sign-in here. A device set up with it loses its data. */
  async function signOut() {
    if (store.config && !signed) {
      if (!confirm(`Auf diesem Gerät abmelden? Die Daten auf dem Gerät werden gelöscht; im ${wordFor(store.config.kind).one} bleibt alles.`)) return;
      await store.signOutAccount();
    } else {
      await api(null, "/api/logout", { method: "POST" }).catch(() => undefined);
    }
    setSigned(null);
    setPhase("sign_in");
  }
  function next() {
    location.hash = "#/";
    if (signed) onSignedIn(signed);
  }

  if (phase === "sign_in") {
    return (
      <Login
        context="Melde dich an, um deine E-Mail-Adresse zu bestätigen. Danach geht es hier weiter."
        onSignedIn={(s) => {
          setSigned(s);
          void confirmIt();
        }}
      />
    );
  }
  if (phase === "busy") return <AuthPage>{<p className="muted">Adresse wird bestätigt …</p>}</AuthPage>;
  return (
    <AuthPage>
      <div className="card">
        <p className={phase.kind === "ok" ? "ok-text" : "error-box"} data-testid="email-confirmed">
          {phase.text}
        </p>
        {phase.kind === "other" && (
          <button className="wide" onClick={signOut} data-testid="email-other">
            Abmelden und als anderes Konto anmelden
          </button>
        )}
        <button className="primary wide" onClick={next} data-testid="email-next">
          Weiter
        </button>
      </div>
    </AuthPage>
  );
}
